import React, { useState, useEffect } from 'react';
import { Plus, ArrowLeft, Loader2, BookOpen, Star, ChevronDown, Eye, EyeOff, Edit, Trash2, ChevronRight, X, Search } from 'lucide-react';
import { IndianRupee } from '../../components/CustomIcons';
import { toast } from 'react-toastify';
import Pagination from '../../components/Pagination';
import CourseDetailsEditor from './CourseDetails';
import PreviewCard from './PreviewCard';
import NoDataFound from '../../components/NoDataFound';
import Button from '../../components/Button';
import ConfirmationPopup from '../../components/ConfirmationPopup';
import CurriculumModal from '../../components/CurriculumModal';
import { postRequest } from '../../utils/networking';
import { apiUrls } from '../../utils/url';
import CommonToastContainer from '../../components/CommonToastContainer';

export default function CourseListPage() {
  const [currentView, setCurrentView] = useState('list');
  const [editingCourse, setEditingCourse] = useState(null);
  const [expandedCourseId, setExpandedCourseId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [totalItems, setTotalItems] = useState(0);

  const [showVisibilityPopup, setShowVisibilityPopup] = useState(false);
  const [confirmingCourseId, setConfirmingCourseId] = useState(null);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [deletingCourseId, setDeletingCourseId] = useState(null);

  const [isCurriculumModalOpen, setIsCurriculumModalOpen] = useState(false);
  const [isLoadingCurriculum, setIsLoadingCurriculum] = useState(false);
  const [currentCurriculum, setCurrentCurriculum] = useState([]);
  const [curriculumCourseName, setCurriculumCourseName] = useState('');
  const [curriculumCourseId, setCurriculumCourseId] = useState(null);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await postRequest(apiUrls.getcourse, { page: 1, limit: 1000 });
      console.log("Courses response:", response);
      if (response.status == "S") {
        const coursesData = response.data || [];
        const formattedCourses = coursesData.map(course => ({
          ...course,
          imgurl: course.imgurl || '',
          price: course.price ? parseFloat(course.price).toFixed(2) : 0
        }));

        setCourses(formattedCourses);
        if (response.message) {
          toast.success(response.message);
        }
      } else {
        setError(response.message);
        setCourses([]);
        toast.error(response.message || "Failed to fetch courses");
      }
    } catch (err) {
      console.error("Error fetching courses:", err);
      setError("Unable to fetch courses. Please try again.");
      setCourses([]);
      toast.error("Unable to fetch courses. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  const filteredCourses = courses.filter(course => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      course.coursename?.toLowerCase().includes(searchLower) ||
      course.description?.toLowerCase().includes(searchLower) ||
      course.catname?.toLowerCase().includes(searchLower) ||
      course.levelname?.toLowerCase().includes(searchLower)
    );
  });

  useEffect(() => {
    setTotalItems(filteredCourses.length);
    setCurrentPage(1);
  }, [filteredCourses.length]);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentCourses = filteredCourses.slice(startIndex, endIndex);

  const handleClearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
    setExpandedCourseId(null);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
    setExpandedCourseId(null);
  };

  const toggleCourseExpand = (courseId, e) => {
    if (e) {
      e.stopPropagation();
    }
    setExpandedCourseId(expandedCourseId === courseId ? null : courseId);
  };

  const handleVisibilityClick = (e, courseId) => {
    e.stopPropagation();
    setConfirmingCourseId(courseId);
    setShowVisibilityPopup(true);
  };

  const handleConfirmToggle = async () => {
    const course = courses.find(c => c.courseid === confirmingCourseId);
    if (course) {
      setCourses(prev => prev.map(c =>
        c.courseid === confirmingCourseId ? { ...c, visible: !c.visible } : c
      ));
      toast.success(`Course ${!course.visible ? 'shown' : 'hidden'} successfully`);
    }
    setShowVisibilityPopup(false);
    setConfirmingCourseId(null);
  };

  const handleCancelToggle = () => {
    setShowVisibilityPopup(false);
    setConfirmingCourseId(null);
  };

  const handleViewDetails = (course, e) => {
    if (e) {
      e.stopPropagation();
    }

    const courseForPreview = {
      courseid: course.courseid,
      coursename: course.coursename,
      description: course.description,
      price: course.price,
      rating: course.rating,
      duration: course.duration,
      students: course.students || 0,
      visible: course.visible,
      imgurl: course.imgurl || '',
      catid: course.catid,
      catname: course.catname,
      levelid: course.levelid,
      levelname: course.levelname,
      category: course.catname,
      level: course.levelname,
      students_enrolled: course.students || 0,
      syllabus: course.syllabus || []
    };

    console.log("Sending to PreviewCard:", courseForPreview);
    setSelectedCourse(courseForPreview);
    setCurrentView('courseDetails');
  };

  const handleEditCourse = (courseId, e) => {
    if (e) {
      e.stopPropagation();
    }
    const course = courses.find(c => c.courseid === courseId);
    if (course) {
      const courseToEdit = {
        courseid: course.courseid,
        coursename: course.coursename || '',
        description: course.description || '',
        price: course.price || 0,
        catid: course.catid || '',
        levelid: course.levelid || '',
        duration: course.duration || '',
        visible: course.visible !== undefined ? course.visible : true,
        invisible: course.visible !== undefined ? !course.visible : false,
        rating: course.rating || 0,
        catname: course.catname || '',
        levelname: course.levelname || '',
        imgurl: course.imgurl || '',
        syllabus: course.syllabus || []
      };
      setEditingCourse(courseToEdit);
      setCurrentView('details');
    } else {
      console.error("Course not found:", courseId);
    }
  };

  const handleAddNewCourse = () => {
    setEditingCourse(null);
    setCurrentView('details');
  };

  const handleSaveCourse = async (courseData) => {
    console.log("handleSaveCourse called with:", courseData);
    try {
      let formData;

      if (courseData instanceof FormData) {
        formData = courseData;
        if (formData.has('price')) {
          const priceValue = formData.get('price');
          const formattedPrice = parseFloat(priceValue).toFixed(2);
          formData.set('price', formattedPrice);
        }
      } else {
        formData = new FormData();
        const courseName = courseData.coursename || '';

        let catid = courseData.catid;
        let levelid = courseData.levelid;

        if (catid && typeof catid === 'object') {
          catid = catid.id || catid.value || '';
        }

        if (levelid && typeof levelid === 'object') {
          levelid = levelid.id || levelid.value || '';
        }

        let invisible = courseData.invisible;
        if (invisible === undefined && courseData.visible !== undefined) {
          invisible = !courseData.visible;
        }

        const formattedPrice = courseData.price ? parseFloat(courseData.price).toFixed(2) : "0.00";

        formData.append('coursename', courseName);
        formData.append('catid', catid || '');
        formData.append('rating', courseData.rating || 0);
        formData.append('description', courseData.description || '');
        formData.append('price', formattedPrice);
        formData.append('invisible', invisible ? 1 : 0);
        formData.append('levelid', levelid || '');
        formData.append('duration', courseData.duration || '');
        formData.append('students', courseData.students || 0);
        formData.append('syllabus', JSON.stringify(courseData.syllabus || []));
        formData.append('imgurl', courseData.imgurl || '');

        if (courseData.courseid && courseData.courseid !== '') {
          formData.append('courseid', courseData.courseid);
        }
      }

      console.log("FormData entries:");
      for (let pair of formData.entries()) {
        console.log(pair[0], pair[1]);
      }

      const isUpdate = formData.has('courseid') && formData.get('courseid') !== '';
      const apiUrl = isUpdate ? apiUrls.courseupdate : apiUrls.coursecreate;

      console.log("Submitting to API:", apiUrl);
      const result = await postRequest(apiUrl, formData);
      console.log("Save course result:", result);

      if (result.status === "S") {
        await fetchCourses();
        const successMessage = result.message || `Course ${isUpdate ? 'updated' : 'created'} successfully`;
        toast.success(successMessage);

        setCurrentView('list');
        setEditingCourse(null);
        return result;
      } else {
        const errorMessage = result.message || `Failed to ${isUpdate ? 'update' : 'create'} course`;
        toast.error(errorMessage);
        return result;
      }
    } catch (err) {
      console.error("Error saving course:", err);
      toast.error("An error occurred while saving the course. Please try again.");
      throw err;
    }
  };

  const handleCancelDetails = () => {
    setCurrentView('list');
    setEditingCourse(null);
  };

  const handleDeleteClick = (courseId, e) => {
    if (e) {
      e.stopPropagation();
    }
    setDeletingCourseId(courseId);
    setShowDeletePopup(true);
  };

  const handleConfirmDelete = async () => {
    if (deletingCourseId) {
      try {
        const response = await postRequest(apiUrls.coursedelete, { courseid: deletingCourseId });
        if (response.status === "S") {
          await fetchCourses();
          setShowDeletePopup(false);
          setDeletingCourseId(null);
          toast.success(response.message || "Course deleted successfully");
        } else {
          toast.error(response.message || "Failed to delete course");
        }
      } catch (err) {
        console.error("Error deleting course:", err);
        toast.error("Failed to delete course");
      }
    }
  };

  const handleCancelDelete = () => {
    setShowDeletePopup(false);
    setDeletingCourseId(null);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setExpandedCourseId(null);
    const tableContainer = document.querySelector('.overflow-y-auto');
    if (tableContainer) {
      tableContainer.scrollTop = 0;
    }
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
    setExpandedCourseId(null);
  };

  const handleViewCurriculum = (course, e) => {
    if (e) {
      e.stopPropagation();
    }

    setCurriculumCourseName(course.coursename);
    setCurriculumCourseId(course.courseid);
    setCurrentView('syllabus');
  };

  const confirmingCourse = courses.find(c => c.courseid === confirmingCourseId);
  const deletingCourse = courses.find(c => c.courseid === deletingCourseId);
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Render different views based on currentView
  const renderContent = () => {
    if (currentView === 'details') {
      return (
        <div className="h-full bg-slate-50">
          <div className="p-4 sm:p-6 lg:p-8">
            <div className="max-w-[1400px] mx-auto">
              <CourseDetailsEditor
                course={editingCourse}
                onSave={handleSaveCourse}
                onCancel={handleCancelDetails}
                onViewCurriculum={(data) => handleViewCurriculum(data)}
                isNew={!editingCourse || !editingCourse.courseid}
              />
            </div>
          </div>
        </div>
      );
    }

    if (currentView === 'courseDetails') {
      console.log("Rendering course details with:", selectedCourse);

      return (
        <div className="h-full bg-slate-50 p-4 sm:p-8 flex flex-col">
          <div className="max-w-[1400px] mx-auto w-full mb-6">
            <button
              onClick={() => setCurrentView('list')}
              className="flex items-center gap-2 text-slate-600 hover:text-orange-500 transition-colors bg-white px-4 py-2 rounded-xl border border-slate-100 shadow-sm"
            >
              <ArrowLeft size={18} />
              Back to Course List
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-8 flex flex-col items-center">
            <div className="w-full max-w-2xl my-auto">
              <div className="mb-6 text-center">
                <h2 className="text-3xl font-bold text-slate-800">Course Preview</h2>
                <p className="text-slate-500">This is how your course appears to students</p>
              </div>
              <div className="bg-white p-4 sm:p-6 rounded-[2.5rem] shadow-2xl border border-slate-100">
                <PreviewCard formData={selectedCourse} onViewCurriculum={(data) => handleViewCurriculum(data)} />
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (currentView === 'syllabus') {
      return (
        <CurriculumModal
          isOpen={true}
          onClose={() => {
            setCurrentView(editingCourse ? 'details' : 'list');
            setCurriculumCourseId(null);
          }}
          initialSyllabus={currentCurriculum}
          onSave={null}
          readOnly={false}
          courseId={curriculumCourseId}
        />
      );
    }

    // Default: list view
    return (
      <div className="bg-slate-50 h-full overflow-hidden">
        <div className="p-2 sm:p-4 lg:p-6 h-full flex flex-col">
          <div className="max-w-[1400px] mx-auto w-full flex-1 flex flex-col min-h-0">
            <div className="bg-white flex flex-col flex-1 min-h-0 rounded-xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="px-3 pt-5 pb-3 border-b border-slate-100">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="relative w-full sm:w-80">
                    <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search courses..."
                      value={searchTerm}
                      onChange={handleSearchChange}
                      className="w-full text-sm pl-10 pr-10 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                    {searchTerm && (
                      <button
                        onClick={handleClearSearch}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                  <div className="flex-shrink-0 w-full sm:w-auto">
                    <Button
                      onClick={handleAddNewCourse}
                      className="w-full sm:w-auto px-4 py-2 text-sm font-medium bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg hover:bg-orange-600 hover:-translate-y-0.5 transition-all active:translate-y-0 cursor-pointer"
                    >
                      <Plus size={18} />
                      Add New Course
                    </Button>
                  </div>
                </div>
              </div>
              <div className="w-full overflow-x-auto flex-1 overflow-y-auto">
                {!loading && currentCourses.length > 0 ? (
                  <table className="w-full min-w-[1000px]">
                    <thead className="bg-slate-50 border-b border-slate-100 sticky top-0 z-30">
                      <tr>
                        <th className="static sm:sticky sm:left-0 bg-slate-50 sm:z-20 py-3 px-4 text-left text-sm font-semibold text-slate-600 w-10"></th>
                        <th className="static sm:sticky sm:left-10 bg-slate-50 sm:z-20 py-3 px-4 text-left text-sm font-semibold text-slate-600 w-16">S.No</th>
                        <th className="static sm:sticky sm:left-24 bg-slate-50 sm:z-20 py-3 px-4 text-left text-sm font-semibold text-slate-600 sm:shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">Course Name</th>
                        <th className="py-3 px-4 text-left text-sm font-semibold text-slate-600">Rating</th>
                        <th className="py-3 px-4 text-left text-sm font-semibold text-slate-600">Description</th>
                        <th className="py-3 px-4 text-left text-sm font-semibold text-slate-600">Price</th>
                        <th className="static sm:sticky sm:right-0 bg-slate-50 sm:z-20 py-3 px-4 text-left text-sm font-semibold text-slate-600 sm:shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)]">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentCourses.map((course, index) => {
                        const isExpanded = expandedCourseId === course.courseid;
                        const serialNumber = startIndex + index + 1;
                        return (
                          <React.Fragment key={course.courseid}>
                            <tr
                              className={`group border-b border-slate-100 transition-colors cursor-pointer ${isExpanded
                                  ? 'bg-slate-50 border-l-4 border-l-orange-500'
                                  : 'bg-white hover:bg-slate-50'
                                }`}
                              onClick={(e) => toggleCourseExpand(course.courseid, e)}
                            >
                              <td className={`py-3 px-4 static sm:sticky sm:left-0 sm:z-10 transition-colors ${isExpanded ? 'bg-slate-50' : 'bg-white group-hover:bg-slate-50'}`}>
                                <button
                                  onClick={(e) => toggleCourseExpand(course.courseid, e)}
                                  className="text-slate-500 hover:text-orange-500 transition-colors cursor-pointer"
                                >
                                  {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                                </button>
                              </td>
                              <td className={`py-3 px-4 static sm:sticky sm:left-10 sm:z-10 transition-colors ${isExpanded ? 'bg-slate-50' : 'bg-white group-hover:bg-slate-50'}`}>
                                <span className="text-sm text-slate-600 font-medium">{serialNumber}</span>
                              </td>
                              <td className={`py-3 px-4 static sm:sticky sm:left-24 sm:z-10 transition-colors sm:shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] ${isExpanded ? 'bg-slate-50' : 'bg-white group-hover:bg-slate-50'}`}>
                                <div className="flex items-center gap-3">
                                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${isExpanded ? 'bg-orange-50' : 'bg-slate-100'
                                    }`}>
                                    <BookOpen size={17} className={`transition-colors ${isExpanded ? 'text-orange-700' : 'text-orange-500'
                                      }`} />
                                  </div>
                                  <span className={`font-medium text-sm transition-colors whitespace-nowrap ${isExpanded ? 'text-orange-800' : 'text-slate-800'
                                    }`}>{course.coursename}</span>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex gap-0.5 whitespace-nowrap">
                                  {[...Array(5)].map((_, i) => (
                                    <Star
                                      key={i}
                                      size={14}
                                      className={`${i < Math.floor(course.rating || 0)
                                          ? 'fill-orange-500 text-orange-500'
                                          : 'fill-slate-200 text-slate-200'
                                        }`}
                                    />
                                  ))}
                                  <span className="text-sm text-slate-500 ml-1">({course.rating || 0})</span>
                                </div>
                              </td>
                              <td className="py-3 px-4 text-slate-600">
                                <p className="text-sm line-clamp-2 min-w-[200px] max-w-sm">{course.description}</p>
                              </td>
                              <td className="py-3 px-4">
                                <span className={`font-semibold text-sm whitespace-nowrap transition-colors flex items-center gap-0.5 ${isExpanded ? 'text-orange-700' : 'text-slate-800'
                                  }`}>
                                  {parseFloat(course.price) === 0 ? (
                                    'Free'
                                  ) : (
                                    <>
                                      <IndianRupee size={13} className="inline-block" />
                                      {parseFloat(course.price).toFixed(2)}
                                    </>
                                  )}
                                </span>
                              </td>
                              <td className={`py-3 px-4 static sm:sticky sm:right-0 sm:z-10 transition-colors sm:shadow-[-2px_0_5px_-2px_rgba(0,0,0,0.1)] ${isExpanded ? 'bg-slate-50' : 'bg-white group-hover:bg-slate-50'}`}>
                                <div className="flex gap-2">
                                  <button
                                    onClick={(e) => handleEditCourse(course.courseid, e)}
                                    className="p-1 text-blue-500 hover:bg-blue-50 rounded transition-colors cursor-pointer"
                                  >
                                    <Edit size={16} />
                                  </button>
                                  <button
                                    onClick={(e) => handleDeleteClick(course.courseid, e)}
                                    className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors cursor-pointer"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </td>
                            </tr>

                            {isExpanded && (
                              <tr className="bg-slate-100/80">
                                <td colSpan="7" className="py-3 px-4">
                                  <div className="mx-2 my-1 px-6 py-3.5 bg-white rounded-lg animate-slideDown overflow-x-auto">
                                    <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-2 min-w-[600px]">
                                      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 flex-1 flex-wrap w-full">
                                        <div className="flex items-center justify-between sm:justify-start gap-1 bg-orange-50 px-3 py-2 rounded-md w-full sm:w-auto">
                                          <span className="text-[10px] text-orange-600 font-semibold capitalize whitespace-nowrap">Category</span>
                                          <span className="text-xs text-slate-700 font-medium">{course.catname || 'Business'}</span>
                                        </div>
                                        <div className="flex items-center justify-between sm:justify-start gap-1 bg-orange-50 px-3 py-2 rounded-md w-full sm:w-auto">
                                          <span className="text-[10px] text-orange-600 font-semibold capitalize whitespace-nowrap">Level</span>
                                          <span className="text-xs text-slate-700 font-medium">{course.levelname || 'Beginner'}</span>
                                        </div>
                                        <div className="flex items-center justify-between sm:justify-start gap-1 bg-orange-50 px-3 py-2 rounded-md w-full sm:w-auto">
                                          <span className="text-[10px] text-orange-600 font-semibold capitalize whitespace-nowrap">Duration</span>
                                          <span className="text-xs text-slate-700 font-medium">{course.duration || 'N/A'}</span>
                                        </div>
                                      </div>
                                      <div className="flex flex-col sm:flex-row items-center gap-2 w-full lg:w-auto">
                                        <button
                                          onClick={(e) => handleViewCurriculum(course, e)}
                                          disabled={isLoadingCurriculum}
                                          className="w-full lg:w-auto px-4 py-2 bg-white border border-orange-500 text-orange-500 rounded-md text-xs font-medium whitespace-nowrap hover:bg-orange-50 transition-colors flex items-center justify-center gap-2"
                                        >
                                          <BookOpen size={14} />
                                          Syllabus
                                        </button>
                                        <button
                                          onClick={(e) => handleViewDetails(course, e)}
                                          className="w-full lg:w-auto px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-md text-xs font-medium whitespace-nowrap hover:opacity-90 transition-opacity"
                                        >
                                          View Full Details
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                ) : loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
                  </div>
                ) : (
                  <NoDataFound searchTerm={searchTerm} />
                )}
              </div>

              {totalItems > 0 && (
                <div className="mt-auto border-t border-slate-100">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    itemsPerPage={itemsPerPage}
                    totalItems={totalItems}
                    onItemsPerPageChange={handleItemsPerPageChange}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Loading state
  if (loading && courses.length === 0) {
    return (
      <div className="h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-orange-500 mx-auto mb-4" />
          <p className="text-slate-600">Loading courses...</p>
        </div>

        {confirmingCourse && (
          <ConfirmationPopup
            isOpen={showVisibilityPopup}
            onClose={handleCancelToggle}
            onConfirm={handleConfirmToggle}
            title="Confirm Visibility Change"
            message={`Are you sure you want to make this course ${confirmingCourse.visible ? 'hidden' : 'visible'}?`}
            confirmText={`Yes, ${confirmingCourse.visible ? 'Hide' : 'Show'} Course`}
            cancelText="Cancel"
          />
        )}

        {deletingCourse && (
          <ConfirmationPopup
            isOpen={showDeletePopup}
            onClose={handleCancelDelete}
            onConfirm={handleConfirmDelete}
            title="Confirm Delete"
            message={
              <>Are you sure you want to delete the course "{deletingCourse.coursename}"?</>
            }
            confirmText="Yes, Iam sure"
            cancelText="Cancel"
          />
        )}

        <CommonToastContainer />
      </div>
    );
  }

  return (
    <>
      {renderContent()}

      {/* Confirmation Popups */}
      {confirmingCourse && (
        <ConfirmationPopup
          isOpen={showVisibilityPopup}
          onClose={handleCancelToggle}
          onConfirm={handleConfirmToggle}
          title="Confirm Visibility Change"
          message={`Are you sure you want to make this course ${confirmingCourse.visible ? 'hidden' : 'visible'}?`}
          confirmText={`Yes, ${confirmingCourse.visible ? 'Hide' : 'Show'} Course`}
          cancelText="Cancel"
        />
      )}

      {deletingCourse && (
        <ConfirmationPopup
          isOpen={showDeletePopup}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          title="Confirm Delete"
          message={
            <>Are you sure you want to delete the course "{deletingCourse.coursename}"?</>
          }
          confirmText="Yes, Iam sure"
          cancelText="Cancel"
        />
      )}

      <CommonToastContainer />
    </>
  );
}